import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FileText, History, User, ChevronDown, LogOut, Zap, Crown, Shield, Target, Briefcase, Mail, Layers } from "lucide-react";
import AuthModal from "@/components/AuthModal";
import UpgradeModal from "@/components/UpgradeModal";

const PLAN_STYLES = {
  free: "bg-[#2a2a38] text-[#8a8799]",
  weekly: "bg-emerald-900/20 text-emerald-400 border border-emerald-400/30",
  pro: "bg-[#c8a96e]/20 text-[#c8a96e] border border-[#c8a96e]/30",
  career: "bg-purple-900/20 text-purple-400 border border-purple-400/30"
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className="sticky top-0 z-50 border-b border-[#2a2a38]"
        style={{ backdropFilter: 'blur(12px)', background: 'rgba(10,10,15,0.85)' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to={user ? "/generate" : "/"} data-testid="logo-link" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded bg-[#c8a96e] flex items-center justify-center">
              <FileText size={16} className="text-black" />
            </div>
            <span className="font-bold text-[#f0ede8] tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Resumiq<span className="text-[#c8a96e]">.in</span>
            </span>
          </Link>

          {/* Nav links + actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/generate" data-testid="nav-generate"
                  className="hidden md:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                  <Zap size={14} /> Generator
                </Link>
                <Link to="/history" data-testid="nav-history"
                  className="hidden md:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                  <History size={14} /> History
                </Link>
                {user.plan === 'career' && (
                  <>
                    <Link to="/job-tracker" data-testid="nav-job-tracker"
                      className="hidden md:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                      <Briefcase size={14} /> Tracker
                    </Link>
                    <Link to="/career/cold-email" data-testid="nav-cold-email"
                      className="hidden lg:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                      <Mail size={14} /> Outreach
                    </Link>
                    <Link to="/career/bulk-generate" data-testid="nav-bulk-generate"
                      className="hidden lg:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                      <Layers size={14} /> Bulk Gen
                    </Link>
                  </>
                )}
                <Link to="/tools/ats-score-checker" data-testid="nav-ats-tool"
                  className="hidden md:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                  <Target size={14} /> ATS Check
                </Link>

                {/* Upgrade button for free users */}
                {(user.plan === 'free') && (
                  <button
                    data-testid="nav-upgrade-btn"
                    onClick={() => setShowUpgrade(true)}
                    className="hidden md:flex items-center gap-1.5 text-xs bg-gradient-to-r from-[#c8a96e] to-[#e8c98e] text-black px-3 py-1.5 rounded-full font-semibold hover:opacity-90 transition-opacity duration-200"
                  >
                    <Crown size={12} /> Upgrade
                  </button>
                )}

                {/* Plan badge */}
                <span
                  data-testid="nav-plan-badge"
                  className={`hidden md:inline text-xs px-2 py-1 rounded-full font-medium cursor-pointer ${PLAN_STYLES[user.plan || 'free']}`}
                  onClick={() => user.plan === 'free' && setShowUpgrade(true)}
                >
                  {(user.plan || 'free').charAt(0).toUpperCase() + (user.plan || 'free').slice(1)}
                </span>

                {/* Avatar dropdown */}
                <div className="relative">
                  <button
                    data-testid="user-avatar-btn"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-1.5 ml-2 group"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#c8a96e]/20 border border-[#c8a96e]/40 flex items-center justify-center text-[#c8a96e] text-xs font-bold transition-colors duration-200 group-hover:border-[#c8a96e]">
                      {initials}
                    </div>
                    <ChevronDown size={14} className={`text-[#8a8799] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {dropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                      <div
                        data-testid="user-dropdown"
                        className="absolute right-0 top-10 w-48 bg-[#13131a] border border-[#2a2a38] rounded-sm shadow-2xl z-50 py-1"
                        style={{ backdropFilter: 'blur(12px)' }}
                      >
                        <button onClick={() => { navigate('/profile'); setDropdownOpen(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-[#f0ede8] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                          <User size={14} className="text-[#8a8799]" /> My Profile
                        </button>
                        <button onClick={() => { navigate('/history'); setDropdownOpen(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-[#f0ede8] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                          <History size={14} className="text-[#8a8799]" /> History
                        </button>
                        <button onClick={() => { navigate('/tools/ats-score-checker'); setDropdownOpen(false); }}
                          data-testid="dropdown-ats-tool"
                          className="w-full text-left px-4 py-2.5 text-sm text-[#f0ede8] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                          <Target size={14} className="text-[#8a8799]" /> ATS Score Checker
                        </button>
                        {user.plan === 'career' && (
                          <>
                            <button onClick={() => { navigate('/job-tracker'); setDropdownOpen(false); }}
                              data-testid="dropdown-job-tracker"
                              className="w-full text-left px-4 py-2.5 text-sm text-[#f0ede8] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                              <Briefcase size={14} className="text-[#8a8799]" /> Job Tracker
                            </button>
                            <button onClick={() => { navigate('/career/cold-email'); setDropdownOpen(false); }}
                              data-testid="dropdown-cold-email"
                              className="w-full text-left px-4 py-2.5 text-sm text-[#f0ede8] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                              <Mail size={14} className="text-[#8a8799]" /> Cold Email Writer
                            </button>
                            <button onClick={() => { navigate('/career/bulk-generate'); setDropdownOpen(false); }}
                              data-testid="dropdown-bulk-generate"
                              className="w-full text-left px-4 py-2.5 text-sm text-[#f0ede8] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                              <Layers size={14} className="text-[#8a8799]" /> Bulk Generate
                            </button>
                          </>
                        )}
                        {user.is_admin && (
                          <button data-testid="nav-admin" onClick={() => { navigate('/admin'); setDropdownOpen(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#c8a96e] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                            <Shield size={14} /> Admin Dashboard
                          </button>
                        )}
                        {(user.plan === 'free') && (
                          <button onClick={() => { setShowUpgrade(true); setDropdownOpen(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#c8a96e] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                            <Crown size={14} /> Upgrade Plan
                          </button>
                        )}
                        <div className="border-t border-[#2a2a38] my-1" />
                        <button onClick={handleLogout}
                          className="w-full text-left px-4 py-2.5 text-sm text-[#f87171] hover:bg-[#1c1c26] flex items-center gap-2 transition-colors duration-150">
                          <LogOut size={14} /> Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/tools/ats-score-checker" data-testid="nav-ats-tool-public"
                  className="hidden md:flex items-center gap-1.5 text-sm text-[#8a8799] hover:text-[#c8a96e] px-3 py-2 transition-colors duration-200">
                  <Target size={14} /> Free ATS Check
                </Link>
                <button
                  data-testid="nav-login-btn"
                  onClick={() => { setAuthMode('login'); setShowAuth(true); }}
                  className="text-sm text-[#8a8799] hover:text-[#f0ede8] px-4 py-2 border border-transparent hover:border-[#2a2a38] rounded-sm transition-all duration-200"
                >
                  Login
                </button>
                <button
                  data-testid="nav-signup-btn"
                  onClick={() => { setAuthMode('signup'); setShowAuth(true); }}
                  className="text-sm bg-[#c8a96e] text-black px-4 py-2 rounded-sm font-medium hover:bg-[#e8c98e] transition-colors duration-200"
                  style={{ boxShadow: '0 0 15px rgba(200,169,110,0.25)' }}
                >
                  Sign up free
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {showAuth && <AuthModal mode={authMode} onClose={() => setShowAuth(false)} />}
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </>
  );
}
